export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const ATTRIBUTION_COOKIE = "cs_referral";
export const ATTRIBUTION_KEYS = [
  "ref",
  "referral",
  "source",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "gclid",
  "fbclid",
  "msclkid",
  "gbraid",
  "wbraid",
  "ttclid",
  // The external site that sent the visitor (no UTM tags) and the first page they opened.
  "referrer",
  "landing",
];

/** `source` values that are our own plumbing, never a marketing source. */
const INTERNAL_SOURCES = ["supabase", "demo"];

/**
 * Hosts that appear as the referrer or source only because sign-in passed through them
 * (Google sign-in, the Supabase auth server) or because the visitor moved between our own sites.
 */
export function isAuthOrOwnHost(host: string): boolean {
  const h = host.trim().toLowerCase().replace(/^www\./, "");
  return (
    h === "callsyncapp.com" ||
    h.endsWith(".callsyncapp.com") ||
    h === "supabase.co" ||
    h.endsWith(".supabase.co") ||
    h.endsWith(".supabase.com") ||
    h === "accounts.google.com" ||
    h === "accounts.youtube.com" ||
    h === "appleid.apple.com" ||
    h === "login.microsoftonline.com" ||
    h === "checkout.razorpay.com" ||
    h.endsWith(".paddle.com")
  );
}

/** The external site in document.referrer, or null for none, our own sites and sign-in pages. */
export function externalReferrer(referrer: string | null | undefined): string | null {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    if (!/^https?:$/.test(url.protocol) || isAuthOrOwnHost(url.hostname)) return null;
    return url.hostname.toLowerCase().replace(/^www\./, "").slice(0, 200);
  } catch {
    return null;
  }
}

/** Drops values that only describe sign-in or our own plumbing, so they never become the source. */
export function cleanAttribution(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = values[key];
    if (typeof value !== "string" || !value) continue;
    if (
      (key === "source" || key === "utm_source" || key === "referrer") &&
      (INTERNAL_SOURCES.includes(value.toLowerCase()) || isAuthOrOwnHost(value))
    )
      continue;
    out[key] = value.slice(0, 200);
  }
  return out;
}
export interface SharedAttribution {
  ts: number;
  values: Record<string, string>;
}
interface CookieContext {
  cookie: string;
}
interface CookieLocation {
  hostname: string;
  protocol: string;
}

function supported(location?: CookieLocation): boolean {
  return (
    location?.protocol === "https:" &&
    ["callsyncapp.com", "www.callsyncapp.com", "dash.callsyncapp.com", "help.callsyncapp.com", "sales.callsyncapp.com"].includes(
      location.hostname,
    )
  );
}

export function readAttributionCookie(
  doc?: CookieContext,
  location?: CookieLocation,
  now = Date.now(),
): SharedAttribution | null {
  if (!doc || !supported(location)) return null;
  try {
    const encoded = doc.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${ATTRIBUTION_COOKIE}=`))
      ?.slice(ATTRIBUTION_COOKIE.length + 1);
    if (!encoded) return null;
    const saved = JSON.parse(decodeURIComponent(encoded));
    if (
      !Number.isFinite(saved.ts) ||
      saved.ts > now + 60_000 ||
      now - saved.ts >= ATTRIBUTION_TTL_MS
    )
      return null;
    const values = cleanAttribution(saved.values || {});
    if (!Object.keys(values).length) return null;
    return { ts: saved.ts, values };
  } catch {
    return null;
  }
}

export function writeAttributionCookie(
  saved: SharedAttribution,
  doc?: CookieContext,
  location?: CookieLocation,
  now = Date.now(),
): void {
  if (!doc || !supported(location)) return;
  const remaining = Math.floor((saved.ts + ATTRIBUTION_TTL_MS - now) / 1000);
  if (remaining <= 0 || saved.ts > now + 60_000) return;
  const values = cleanAttribution(saved.values);
  if (!Object.keys(values).length) return;
  try {
    let encoded = encodeURIComponent(JSON.stringify({ ts: saved.ts, values }));
    // Browsers limit cookies to roughly 4 KB, including encoded Unicode values.
    while (encoded.length > 3500) {
      let shortened = false;
      for (const key of Object.keys(values)) {
        if (['gclid', 'fbclid', 'msclkid', 'gbraid', 'wbraid', 'ttclid'].includes(key) || values[key].length <= 1) continue;
        values[key] = values[key].slice(0, Math.max(1, Math.floor(values[key].length / 2)));
        shortened = true;
      }
      if (!shortened) return;
      encoded = encodeURIComponent(JSON.stringify({ ts: saved.ts, values }));
    }
    doc.cookie = `${ATTRIBUTION_COOKIE}=${encoded}; Domain=callsyncapp.com; Path=/; Max-Age=${remaining}; SameSite=Lax; Secure`;
  } catch {
    /* URL forwarding and local storage remain available. */
  }
}

/** Restore Meta's click cookie when the visitor opens the dashboard without fbclid in its URL. */
export function restoreFacebookClickCookie(
  doc?: CookieContext,
  location?: CookieLocation,
  now = Date.now(),
): void {
  const saved = readAttributionCookie(doc, location, now);
  const id = saved?.values.fbclid;
  if (!doc || !saved || !id || !/^[A-Za-z0-9_-]+$/.test(id)) return;
  const value = `fb.1.${saved.ts}.${id}`;
  const current = doc.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("_fbc="));
  if (current === `_fbc=${value}`) return;
  try {
    doc.cookie = `_fbc=${value}; Domain=callsyncapp.com; Path=/; Max-Age=${Math.floor((saved.ts + ATTRIBUTION_TTL_MS - now) / 1000)}; SameSite=Lax; Secure`;
  } catch {
    /* Ad blockers or browser policies can prevent tracking. */
  }
}

/**
 * Keep one Meta browser ID (_fbp) for every callsyncapp.com subdomain, so the visitor from an ad on
 * the website is the same browser at dashboard signup and checkout. A host-only copy would shadow it.
 */
export function shareFacebookBrowserCookie(
  doc?: CookieContext,
  location?: CookieLocation,
  now = Date.now(),
  random = Math.random,
): void {
  if (!doc || !supported(location)) return;
  try {
    const current = doc.cookie
      .split(";")
      .map((part) => part.trim())
      .filter((part) => part.startsWith("_fbp="))
      .map((part) => part.slice(5))
      .find((value) => /^fb\.[0-2]\.\d{10,13}\.[\w.-]{1,100}$/.test(value));
    const value = current || `fb.1.${now}.${Math.round(2147483647 * random())}`;
    doc.cookie = "_fbp=; Path=/; Max-Age=0; SameSite=Lax; Secure";
    doc.cookie = `_fbp=${value}; Domain=callsyncapp.com; Path=/; Max-Age=7776000; SameSite=Lax; Secure`;
  } catch {
    /* Browser policies can block tracking cookies. */
  }
}
